import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Stack,
  Button,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import AudiotrackOutlinedIcon from "@mui/icons-material/AudiotrackOutlined";
import { useTranslation } from "react-i18next";
import { uploadOne } from "../../../hooks/useS3Upload";
import { pickLocalFiles } from "../../../utils/pickLocalFiles";
import { useTeamBoardAction } from "../hooks/useTeamBoardAction";
import { attachmentKind, isPreviewableAttachment } from "../utils/taskAttachmentKind";
import TaskAttachmentLightbox from "./TaskAttachmentLightbox";

function formatFileSize(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function KindIcon({ kind }) {
  if (kind === "pdf") return <PictureAsPdfOutlinedIcon fontSize="small" color="error" />;
  if (kind === "video") return <VideocamOutlinedIcon fontSize="small" color="primary" />;
  if (kind === "audio") return <AudiotrackOutlinedIcon fontSize="small" color="primary" />;
  return <InsertDriveFileOutlinedIcon fontSize="small" color="action" />;
}

function AttachmentCard({ att, canManage, busy, onOpen, onDelete, t, theme }) {
  const kind = attachmentKind(att);
  const previewable = isPreviewableAttachment(att);
  const open = () => {
    if (previewable) onOpen();
    else if (att.fileUrl) window.open(att.fileUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: 2,
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: alpha(theme.palette.background.default, 0.6),
        opacity: busy ? 0.6 : 1,
        "&:hover .attachment-delete": { opacity: 1 },
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open();
          }
        }}
        aria-label={previewable ? t("attachmentPreviewAria") : t("attachmentOpenFile")}
        sx={{ display: "block", width: "100%", cursor: "pointer", textAlign: "left" }}
      >
        {kind === "image" ? (
          <Box
            component="img"
            src={att.fileUrl}
            alt={att.fileName || ""}
            sx={{
              width: "100%",
              height: 168,
              objectFit: "contain",
              display: "block",
              bgcolor: "action.hover",
            }}
          />
        ) : null}
        {kind === "video" ? (
          <Box sx={{ position: "relative", bgcolor: "#111", height: 168 }}>
            <Box
              component="video"
              src={att.fileUrl}
              muted
              preload="metadata"
              sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha("#000", 0.28),
                color: "common.white",
              }}
            >
              <VideocamOutlinedIcon />
            </Box>
          </Box>
        ) : null}
        {kind === "audio" ? (
          <Box sx={{ px: 1.25, pt: 1.25 }}>
            <Box
              component="audio"
              src={att.fileUrl}
              controls
              onClick={(e) => e.stopPropagation()}
              sx={{ width: "100%" }}
            />
          </Box>
        ) : null}
        {kind === "pdf" || kind === "file" ? (
          <Box
            sx={{
              height: kind === "pdf" ? 168 : 88,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.primary.main, kind === "pdf" ? 0.08 : 0.04),
              color: kind === "pdf" ? "error.main" : "primary.main",
            }}
          >
            <KindIcon kind={kind} />
          </Box>
        ) : null}
        <Box sx={{ px: 1.25, py: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {att.fileName || t("attachmentOpenFile")}
          </Typography>
          {att.sizeBytes ? (
            <Typography variant="caption" color="text.secondary">
              {formatFileSize(att.sizeBytes)}
            </Typography>
          ) : null}
        </Box>
      </Box>
      {canManage ? (
        <IconButton
          className="attachment-delete"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          disabled={busy}
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            opacity: busy ? 1 : 0,
            transition: "opacity 0.12s",
            bgcolor: alpha(theme.palette.background.paper, 0.92),
            "&:hover": { bgcolor: "background.paper" },
          }}
          aria-label={t("deleteAttachment")}
        >
          {busy ? <CircularProgress size={16} /> : <DeleteOutlineIcon fontSize="small" />}
        </IconButton>
      ) : null}
    </Box>
  );
}

function normalizeFiles(input) {
  if (!input) return [];
  return (Array.isArray(input) ? input : [input]).filter(Boolean);
}

const TaskCardAttachments = forwardRef(function TaskCardAttachments(
  { attachments = [], canManage, boardId, cardId, mutations },
  ref
) {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();
  const { run, showError } = useTeamBoardAction();
  const dragDepthRef = useRef(0);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(null);

  const previewableAttachments = useMemo(
    () => (attachments || []).filter(isPreviewableAttachment),
    [attachments]
  );

  const uploadFile = useCallback(
    async (file) => {
      if (!file || !mutations?.createAttachment) return;

      const prefix = `team-boards/${boardId}/${cardId}`;
      const { url, key } = await uploadOne(file, { prefix });
      await run(
        () =>
          mutations.createAttachment.mutateAsync({
            cardId,
            fileName: file.name,
            fileUrl: url,
            fileKey: key,
            mimeType: file.type || null,
            sizeBytes: file.size,
          }),
        { context: "attachment", successKey: "successAttachmentAdded" }
      );
    },
    [boardId, cardId, mutations, run]
  );

  const uploadFiles = useCallback(
    async (input) => {
      const files = normalizeFiles(input);
      if (!files.length || !canManage || uploading) return;

      setUploading(true);
      try {
        for (const file of files) {
          await uploadFile(file);
        }
      } catch (err) {
        console.error("[TaskCardAttachments] upload:", err);
        showError(err, "upload");
      } finally {
        setUploading(false);
      }
    },
    [canManage, uploadFile, uploading, showError]
  );

  const pickAndUpload = useCallback(async () => {
    if (!canManage || uploading) return;
    const files = await pickLocalFiles({ multiple: true });
    if (files.length) await uploadFiles(files);
  }, [canManage, uploading, uploadFiles]);

  useImperativeHandle(
    ref,
    () => ({
      uploadFiles,
      openFilePicker: () => {
        void pickAndUpload();
      },
    }),
    [uploadFiles, pickAndUpload]
  );

  const handlePickFile = () => {
    void pickAndUpload();
  };

  const handleDragEnter = (event) => {
    if (!canManage || uploading) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    if (dragDepthRef.current === 1) setIsDragging(true);
  };

  const handleDragOver = (event) => {
    if (!canManage || uploading) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    if (!canManage || uploading) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  };

  const handleDrop = async (event) => {
    if (!canManage || uploading) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragging(false);

    const files = Array.from(event.dataTransfer?.files || []);
    await uploadFiles(files);
  };

  const handleDelete = async (attachmentId) => {
    if (!mutations?.deleteAttachment || deletingId) return;
    setDeletingId(attachmentId);
    try {
      await run(
        () => mutations.deleteAttachment.mutateAsync({ cardId, attachmentId }),
        { context: "attachment" }
      );
    } finally {
      setDeletingId(null);
    }
  };

  if (!attachments.length && !canManage) return null;

  return (
    <Box sx={{ mb: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <AttachFileOutlinedIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" fontWeight={700}>
          {t("attachmentsSection")}
        </Typography>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fill, minmax(200px, 1fr))" },
          gap: 1.25,
        }}
      >
        {attachments.map((att) => (
          <AttachmentCard
            key={att.id}
            att={att}
            canManage={canManage}
            busy={deletingId === att.id || uploading}
            t={t}
            theme={theme}
            onOpen={() => {
              const idx = previewableAttachments.findIndex((item) => item.id === att.id);
              if (idx >= 0) setPreviewIndex(idx);
            }}
            onDelete={() => handleDelete(att.id)}
          />
        ))}
      </Box>

      <TaskAttachmentLightbox
        attachments={previewableAttachments}
        index={previewIndex}
        onClose={() => setPreviewIndex(null)}
        onIndexChange={setPreviewIndex}
      />

      {canManage ? (
        <Box
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            mt: 1.25,
            p: 1.5,
            borderRadius: 2,
            border: `1px dashed ${
              isDragging ? theme.palette.primary.main : theme.palette.divider
            }`,
            bgcolor: isDragging
              ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.08)
              : alpha(theme.palette.background.default, 0.35),
            transition: "border-color 0.15s ease, background-color 0.15s ease",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                flexShrink: 0,
              }}
            >
              <CloudUploadOutlinedIcon fontSize="small" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>
                {isDragging ? t("attachmentDropActive") : t("attachmentDropHint")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("attachmentDropHintSecondary")}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={uploading ? <CircularProgress size={14} /> : <AttachFileOutlinedIcon />}
              onClick={handlePickFile}
              disabled={uploading || Boolean(deletingId)}
              sx={{ fontWeight: 600, borderRadius: 2, flexShrink: 0 }}
            >
              {uploading ? t("attachmentUploading") : t("attachmentAdd")}
            </Button>
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
});

export default TaskCardAttachments;
